import { useRef } from 'react';
import { FolderUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button, type ButtonProps } from '@/components/ui/Button';
import {
  FOLDER_INPUT_PROPS,
  flattenPicked,
  matchesAccept,
  pickedFromFileList,
} from '@/lib/fileTree';

interface FolderUploadButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /** Receives every file inside the picked folder (and its subfolders), flattened. */
  onFiles: (files: File[]) => void;
  /**
   * Same syntax as `<input accept>`. Folder pickers ignore `accept`, so the
   * filter is applied here after the pick; files that don't match are
   * dropped and the user is told how many were skipped.
   */
  accept?: string;
  label?: string;
}

/**
 * "Add folder" companion to a plain multi-file picker, for lists that have
 * no folder hierarchy of their own: the folder's contents are flattened
 * into one upload batch.
 */
export function FolderUploadButton({
  onFiles,
  accept,
  label = 'Add folder',
  variant = 'outline',
  ...buttonProps
}: FolderUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button
        type="button"
        variant={variant}
        onClick={() => inputRef.current?.click()}
        aria-label={label}
        title={label}
        {...buttonProps}
      >
        <FolderUp className="h-4 w-4" />
        <span className="sr-only sm:not-sr-only">{label}</span>
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        tabIndex={-1}
        {...FOLDER_INPUT_PROPS}
        onChange={(event) => {
          // `input.files` is a live FileList that resetting `value`
          // empties in place, so read it before the reset.
          const all = flattenPicked(pickedFromFileList(event.target.files ?? []));
          event.target.value = '';
          const kept = all.filter((file) => matchesAccept(file, accept));
          const skipped = all.length - kept.length;
          if (!kept.length) {
            toast.info(
              skipped
                ? 'Nothing in that folder matches the accepted file types.'
                : 'That folder is empty.',
            );
            return;
          }
          if (skipped) {
            toast.info(`Skipped ${skipped} ${skipped === 1 ? 'file' : 'files'} of another type.`);
          }
          onFiles(kept);
        }}
      />
    </>
  );
}
